import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import apiService from "../../services/api";
import Toast from "../../components/Toast";
import TablePagination from "../company/JobManagement/Components/TablePagination";

interface Conversation {
  id: string;
  subject: string;
  company_name: string;
  status: string;
  priority: string;
  last_message_at: string;
  last_message_by: string;
  message_count: number;
  job_seeker_unread_count: number;
  latest_message?: {
    content: string;
    sender_name: string;
    created_at: string;
  };
  job_title?: string;
}

const CandidateMessages: React.FC = () => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState("active");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "warning" | "info";
    isVisible: boolean;
  }>({
    message: "",
    type: "success",
    isVisible: false,
  });
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedStatus]);

  useEffect(() => {
    loadConversations(currentPage, rowsPerPage);
  }, [selectedStatus, currentPage, rowsPerPage]);

  const loadConversations = async (page = currentPage, limit = rowsPerPage) => {
    try {
      setLoading(true);
      const offset = (page - 1) * limit;
      const response = await apiService.getCandidateMessages(selectedStatus, limit, offset);
      setConversations(response.conversations || []);
      setTotalCount(response.total ?? response.total_count ?? 0);
    } catch (error) {
      console.error("Failed to load conversations:", error);
      showToast("Failed to load conversations", "error");
    } finally {
      setLoading(false);
    }
  };

  const showToast = (
    message: string,
    type: "success" | "error" | "warning" | "info",
  ) => {
    setToast({ message, type, isVisible: true });
  };

  const closeToast = () => {
    setToast((prev) => ({ ...prev, isVisible: false }));
  };

  const handleConversationClick = (conversationId: string) => {
    navigate(`/candidate/messages/${conversationId}`);
  };

  const updateConversationStatus = async (
    conversationId: string,
    newStatus: "active" | "archived" | "closed",
  ) => {
    try {
      await apiService.updateCandidateConversationStatus(
        conversationId,
        newStatus,
      );

      // Update the conversation in the local state
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === conversationId ? { ...conv, status: newStatus } : conv,
        ),
      );

      // Close dropdown
      setActiveDropdown(null);

      // Show success message
      showToast(`Conversation moved to ${newStatus}`, "success");

      // Reload conversations to update the filtered list
      loadConversations(currentPage, rowsPerPage);
    } catch (error) {
      console.error("Failed to update conversation status:", error);
      showToast("Failed to update conversation status", "error");
    }
  };

  const toggleDropdown = (conversationId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent conversation click
    setActiveDropdown(
      activeDropdown === conversationId ? null : conversationId,
    );
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setActiveDropdown(null);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "urgent":
        return "bg-red-100 text-red-800 border-red-200";
      case "high":
        return "bg-orange-100 text-orange-800 border-orange-200";
      case "normal":
        return "bg-gray-100 text-gray-800 border-gray-200";
      case "low":
        return "bg-blue-100 text-blue-800 border-blue-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const filteredConversations = conversations.filter((conversation) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      conversation.subject.toLowerCase().includes(query) ||
      conversation.company_name.toLowerCase().includes(query) ||
      conversation.latest_message?.content.toLowerCase().includes(query)
    );
  });

  const totalUnreadCount = conversations.reduce(
    (sum, conv) => sum + conv.job_seeker_unread_count,
    0,
  );

  if (loading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    <div className="h-3 bg-gray-200 rounded w-full"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto h-screen flex flex-col">
      {/* Header */}
      <div className="mb-6 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Messages</h1>
            <p className="text-gray-600 mt-1">
              Conversations with companies and recruiters
            </p>
          </div>
          {totalUnreadCount > 0 && (
            <div className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm font-medium">
              {totalUnreadCount} unread
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4 flex-shrink-0">
        {/* Status Filter */}
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
          {["active", "archived", "closed"].map((status) => (
            <button
              key={status}
              onClick={() => setSelectedStatus(status)}
              className={`px-3 py-1 rounded-md text-sm font-medium capitalize transition-colors ${
                selectedStatus === status
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              {status}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="flex-1 max-w-md">
          <div className="relative">
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Conversations List - With scroll container */}
      <div className="flex-1 overflow-hidden">
        {filteredConversations.length > 0 ? (
          <div className="h-full overflow-y-auto space-y-3 pr-2 pb-2">
            {filteredConversations.map((conversation) => (
              <div
                key={conversation.id}
                onClick={() => handleConversationClick(conversation.id)}
                className={`bg-white border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
                  conversation.job_seeker_unread_count > 0
                    ? "border-emerald-200 bg-emerald-50/30"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1 min-w-0">
                    {/* Company Avatar */}
                    <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-medium">
                        {conversation.company_name.charAt(0).toUpperCase()}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      {/* Subject and badges row */}
                      <div className="flex items-center space-x-2 mb-1">
                        <h3
                          className={`font-medium text-sm truncate flex-1 ${
                            conversation.job_seeker_unread_count > 0
                              ? "text-gray-900"
                              : "text-gray-700"
                          }`}
                          title={conversation.subject}
                        >
                          {conversation.subject}
                        </h3>
                        <div className="flex items-center space-x-1 flex-shrink-0">
                          <span
                            className={`px-2 py-0.5 text-xs font-medium rounded-full border ${getPriorityColor(
                              conversation.priority,
                            )}`}
                          >
                            {conversation.priority}
                          </span>
                          {conversation.job_seeker_unread_count > 0 && (
                            <span className="bg-emerald-600 text-white text-xs font-medium px-2 py-0.5 rounded-full">
                              {conversation.job_seeker_unread_count}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Company and metadata row */}
                      <div className="flex items-center space-x-3 text-xs text-gray-600 mb-2 overflow-hidden">
                        <span className="flex items-center flex-shrink-0">
                          <span className="mr-1">🏢</span>
                          <span
                            className="truncate max-w-[120px]"
                            title={conversation.company_name}
                          >
                            {conversation.company_name}
                          </span>
                        </span>
                        <span className="flex items-center flex-shrink-0">
                          <span className="mr-1">💬</span>
                          {conversation.message_count} msg
                          {conversation.message_count > 1 ? "s" : ""}
                        </span>
                        {conversation.job_title && (
                          <span className="flex items-center min-w-0">
                            <span className="mr-1 flex-shrink-0">💼</span>
                            <span
                              className="truncate max-w-[150px]"
                              title={conversation.job_title}
                            >
                              {conversation.job_title}
                            </span>
                          </span>
                        )}
                      </div>

                      {/* Latest message preview */}
                      {conversation.latest_message && (
                        <div className="text-sm text-gray-600 truncate">
                          <span className="font-medium">
                            {conversation.latest_message.sender_name}:
                          </span>{" "}
                          <span
                            className="truncate"
                            title={conversation.latest_message.content}
                          >
                            {conversation.latest_message.content}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Status and Actions */}
                  <div className="flex items-start space-x-2 ml-3 flex-shrink-0">
                    <div className="flex flex-col items-end space-y-1 text-right">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          conversation.status === "active"
                            ? "bg-green-100 text-green-800"
                            : conversation.status === "archived"
                            ? "bg-gray-100 text-gray-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {conversation.status}
                      </span>
                    </div>

                    {/* Three-dot menu */}
                    <div className="relative">
                      <button
                        onClick={(e) => toggleDropdown(conversation.id, e)}
                        className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                        title="Conversation options"
                      >
                        <svg
                          className="w-4 h-4 text-gray-500"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                        </svg>
                      </button>

                      {/* Dropdown menu */}
                      {activeDropdown === conversation.id && (
                        <div className="absolute right-0 top-8 z-10 w-36 bg-white rounded-md shadow-lg border border-gray-200">
                          <div className="py-1">
                            {conversation.status !== "active" && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateConversationStatus(
                                    conversation.id,
                                    "active",
                                  );
                                }}
                                className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                <svg
                                  className="w-4 h-4 mr-2 text-green-500"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                Mark Active
                              </button>
                            )}
                            {conversation.status !== "archived" && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateConversationStatus(
                                    conversation.id,
                                    "archived",
                                  );
                                }}
                                className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                <svg
                                  className="w-4 h-4 mr-2 text-gray-500"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" />
                                  <path
                                    fillRule="evenodd"
                                    d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                Archive
                              </button>
                            )}
                            {conversation.status !== "closed" && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateConversationStatus(
                                    conversation.id,
                                    "closed",
                                  );
                                }}
                                className="flex items-center w-full px-3 py-2 text-sm text-red-700 hover:bg-red-50"
                              >
                                <svg
                                  className="w-4 h-4 mr-2 text-red-500"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                Close
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 h-full flex flex-col justify-center">
            <div className="text-6xl mb-4">
              {searchQuery.trim() ? "🔍" : "💬"}
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchQuery.trim()
                ? "No conversations match your search"
                : `No ${selectedStatus} conversations`}
            </h3>
            <p className="text-gray-500 mb-6">
              {searchQuery.trim()
                ? "Try adjusting your search terms to find what you're looking for."
                : selectedStatus === "active"
                ? "When companies reach out to you, their messages will appear here."
                : `No ${selectedStatus} conversations found.`}
            </p>
            {searchQuery.trim() && (
              <button
                onClick={() => setSearchQuery("")}
                className="text-emerald-600 hover:text-emerald-700 font-medium"
              >
                Clear search
              </button>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalCount > rowsPerPage &&(
      <div className="flex-shrink-0">
        <TablePagination
          currentPage={currentPage}
          totalCount={totalCount}
          rowsPerPage={rowsPerPage}
          onPageChange={(page) => setCurrentPage(page)}
          onRowsPerPageChange={(rows) => { setRowsPerPage(rows); setCurrentPage(1); }}
        />
      </div>
       )}
      {/* Toast Notification */}
      {toast.isVisible && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={toast.isVisible}
          onClose={closeToast}
        />
      )}
    </div>
  );
};

export default CandidateMessages;
